<template>
  <MasterBlog v-if="slugType == 'post' && pageLoaded" :data="dataSinglePost" ></MasterBlog>
  <MasterTemplate v-if="slugType == 'page' && pageLoaded && data_master_template" :data="data_master_template"></MasterTemplate>
  <SeoWebPage v-if="slugType == 'page' && pageLoaded && data_seo_webpage" :data="data_seo_webpage"></SeoWebPage>
  <DefaultTemplate v-if="slugType == 'page' && pageLoaded && dataDefault" :data="dataDefault"></DefaultTemplate>
  <SaleCampaignTemplate v-if="slugType == 'page' && pageLoaded && data_campaign" :data="data_campaign" :pageTitle="pageTitle" :currencySetting="currencySetting"></SaleCampaignTemplate>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
  // import MasterBlog from '../components/Blog/MasterBlog.vue';
  // import MasterTemplate from "../components/TemplateMaster/MasterTemplate.vue";
  // import DefaultTemplate from "../components/Page/default.vue";
  // import SaleCampaignTemplate from "../components/Campaign/Sale.vue"

  const MasterBlog = defineAsyncComponent(() => import("../components/Blog/MasterBlog.vue"));
  const MasterTemplate = defineAsyncComponent(() => import("../components/TemplateMaster/MasterTemplate.vue"));
  const SeoWebPage = defineAsyncComponent(() => import("../components/Webpage/SeoWebPage.vue"));
  const DefaultTemplate = defineAsyncComponent(() => import("../components/Page/default.vue"));
  const SaleCampaignTemplate = defineAsyncComponent(() => import("../components/Campaign/Sale.vue"));
  const { pushToDataLayer, pushPageTypeToDataLayer } = useCustomGtm();

  const pageLoaded = ref(false);
  const { $file } = useNuxtApp();
  const userStore = useUserStore();
  const dataSinglePost = ref([]);
  const router = useRouter();
  const route = useRoute();
  const data_master_template = ref();
  const data_campaign = ref();
  const data_seo_webpage = ref();
  const dataDefault = ref();
  const pageTitle = ref('');
  const currencySetting = ref(null);
  const { locale } = useI18n(); 
  const pageSeoInfo = usePageSeoInfo();

  interface IResponseData {
    success: boolean;
    data: string;
    page_template?: string;
    page_title?: string;
    lang?: string;
    translations?: Record<string, any>;
    status?: string;
  }

definePageMeta({
  middleware: 'page-slug'
})

const slugType = useState('slugType');
const isPrivate = useState('isPrivate') || false;

const getFile = async (path: string) => {
  return $file.get(path, {
    headers: {
      Authorization: `Bearer ${userStore.csrf}`,
      'X-API-Server': userStore.csrf_server
    },
  });
};

const extractSlugFromFile = (filename: string): string => {
  // ms_seo-page-demo.json → seo-page-demo
  // seo-page.json → seo-page
  return filename.replace(/^(en_|ms_)?(.+)\.json$/, '$2');
};

const goto404 = () => {
  const notFoundPath = locale.value === 'ms-my' ? '/ms-my/404' : '/404/';
  router.push(notFoundPath);
};
const getTargetLang = () => (locale.value === 'ms-my' ? 'ms-my' : 'en');
const fetchPosts = async () => {
  try {
    const pageId = route.query.p;
    const previewId = route.query.preview_id;
    const preview = route.query.preview;

    const isAllowedPreview =
      preview === 'true' &&
      userStore.isLogin &&
      ['administrator', 'editor'].includes(userStore.user?.role);
    const pageTranslations = usePageTranslations();

    if (slugType.value == 'page') {
      let page_file = `pages/${route.params.slug}.json`;
      if (previewId && isAllowedPreview) {
        page_file = `preview/${previewId}-preview.json`;
      }

      // 👉 key phải unique theo slug, tránh Nuxt trả cache của trang trước
      const { data: masterTemplate } = await useAsyncData(
        `master-template-${route.params.slug}`,
        () => getFile(page_file),
        { watch: [() => route.params.slug] } // 👉 force refetch khi slug đổi
      );

      const response_Template = masterTemplate.value as IResponseData;
      if (!response_Template?.success) return;

      pageLoaded.value = true;

      switch (response_Template.page_template) {
        case 'template-master':
          data_master_template.value = response_Template;
          pageTranslations.value = null; // reset
          pageSeoInfo.value = { pageTemplate: 'template-master', availableLangs: ['en'] };
          break;

        case 'template-sale-campaign':
          data_campaign.value = response_Template.data;
          pageTitle.value = response_Template.page_title || '';
          pageTranslations.value = null; // reset
          pageSeoInfo.value = { pageTemplate: 'template-sale-campaign', availableLangs: ['en'] };
          break;

        case 'seo-webpage': {
          const targetLang = getTargetLang();
          const currentLang = response_Template?.lang || 'en';
          const currentSlug = route.params.slug;

          if (currentLang && currentLang !== targetLang) {
            // Language mismatch - try to find translation
            const translation = response_Template?.translations?.[targetLang];
            
            if (!translation?.file) {
              // No translation found for target language
              
              // Fallback 1: EN → MS (if current is EN and user wants MS)
              if (currentLang === 'en' && targetLang === 'ms-my') {
                console.log(`No MS translation defined, using EN data as fallback`);
                data_seo_webpage.value = response_Template.data;
                pageTranslations.value = response_Template?.translations ?? null;
                pageSeoInfo.value = { pageTemplate: 'seo-webpage', availableLangs: ['en'] };
              } 
              // Fallback 2: MS → EN (if current is MS and user wants EN)
              else if (currentLang === 'ms-my' && targetLang !== 'ms-my') {
                console.log(`No EN translation defined, using MS data as fallback for EN view`);
                data_seo_webpage.value = response_Template.data;
                pageTranslations.value = response_Template?.translations ?? null;
                pageSeoInfo.value = { pageTemplate: 'seo-webpage', availableLangs: ['ms-my'] };
              } 
              else {
                // No fallback rule matches → 404
                goto404();
                return;
              }
            } else {
              // Translation file is defined, try to load it
              const translationSlug = extractSlugFromFile(translation.file);
              if (translationSlug !== currentSlug) {
                // File mismatch → 404
                console.log(`Translation file slug "${translationSlug}" doesn't match current slug "${currentSlug}"`);
                goto404();
                return;
              }

              try {
                const translatedPageData = await getFile(`pages/${translation.file}`);
                const translated = translatedPageData as IResponseData;
                
                if (!translated?.success) {
                  goto404();
                  return;
                }
                
                data_seo_webpage.value = translated.data;
                pageTranslations.value = translated?.translations ?? null;
                pageSeoInfo.value = { pageTemplate: 'seo-webpage', availableLangs: ['en', 'ms-my'] };
              } catch (e) {
                // console.log(`Translation file "${translation.file}" not found:`, e);
                goto404();
                return;
              }
            }
          } else {
            data_seo_webpage.value = response_Template.data;
            pageTranslations.value = response_Template?.translations ?? null;

            const otherLang = currentLang === 'ms-my' ? 'en' : 'ms-my';
            const hasOtherTranslation = !!response_Template?.translations?.[otherLang]?.file;
            if (hasOtherTranslation) {
              pageSeoInfo.value = { pageTemplate: 'seo-webpage', availableLangs: ['en', 'ms-my'] };
            } else {
              pageSeoInfo.value = { pageTemplate: 'seo-webpage', availableLangs: [currentLang === 'ms-my' ? 'ms-my' : 'en'] };
            }
          }
          break;
        }

        default:
          dataDefault.value = response_Template;
          pageTranslations.value = null; // reset
          pageSeoInfo.value = { pageTemplate: response_Template?.page_template || 'default', availableLangs: ['en'] };
      }
    }

    if (slugType.value == 'post') {
      let file_name = `single-post/${route.params.slug}.json`;
      if (pageId && isAllowedPreview) {
        file_name = `preview/${pageId}-preview.json`;
      }

      const { data: singlePost } = await useAsyncData(
        `singlePost-${route.params.slug}`,
        () => getFile(file_name),
        { watch: [() => route.params.slug] }
      );

      if (!singlePost.value) return;
      pageLoaded.value = true;
      pageTranslations.value = null; // reset, post không có translations map
      pageSeoInfo.value = { pageTemplate: 'post', availableLangs: ['en'] };

      const currentPost = singlePost.value as any;
      const postStatus = currentPost?.status ?? '';

      if (postStatus === 'private') {
        if (userStore.isLogin && ['administrator', 'editor'].includes(userStore.user?.role)) {
          dataSinglePost.value = currentPost;
        } else {
          goto404();
        }
      } else if (postStatus === 'publish') {
        dataSinglePost.value = currentPost;
      } else {
        goto404();
      }
    }
  } catch (e) {
    console.error(e);
  }
};

watch(() => route.params.slug, () => {
  fetchPosts();
}, { immediate: true });

  watch(() => route.params.slug, () => {
    fetchPosts();
  }, { immediate: true });

  onMounted(() => {
    if( isPrivate.value && ( ! userStore.isLogin || userStore.isLogin && userStore.user?.role !== 'administrator') ) {
      const notFoundPath = locale.value === 'ms-my' ? '/ms-my/404' : '/404/';
      router.push(notFoundPath);
      return;
    }

    if( slugType.value == 'post' ) {
      const route = useRoute();
      const pageSlug = route.name ? String(route.name) : 'n/a';
      pushPageTypeToDataLayer('other', {
        hierachy1: pageSlug,
        hierachy2: 'n/a',
        hierachy3: 'n/a',
      });
    }

    altLazyIO();
})
  </script>